import Constants from 'utils/Constants';
import NavigationUtils from 'utils/NavigationUtils';
import ProcessUtils from 'utils/ProcessUtils';
import ProcessService from 'services/ProcessService';
import TabsService from 'services/TabsService';
import SecurityService from 'services/SecurityService';
import _ from 'lodash';
import DodService from 'services/DodService';
import DataExplorerService from 'services/DataExplorerService';

// Tell eslint that Array is a global var
/*global Array */

/**
 * Class used to edit process fields in a transactional manner such that the redux store will
 * properly recognize changes.
 * Users must call ProcessEditor.commit() in order to commit the edit and push data to the store.
 */
export default class ProcessEditor {

    /**
     * Contructor will clone a deep copy in which to edit and keep a reference of the original process name
     * (in case the process is renamed).
     * @param process
     */
    constructor(process) {
        this.process = _.cloneDeep(process);
        this.processId = process.name;
        this.postCommitCallbacks = [];
        this.dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;

        // Also make a copy of the ui state in case we have to add flags/info
        // TODO: if this turns out to affect performance, we can only copy the ui state on demand
        const state = ProcessService.getMyState(ProcessService.getStore().getState());
        const processUI = state.processUIStateById[process.name];
        this.processUI = _.cloneDeep(processUI);

    }

    commit() {
        ProcessService.updateProcess(this.processId, this.process, this.processUI, this.postCommitCallbacks);
    }

    /**
     * This is the generic fallback for updating a single field in your process with no special
     * handler logic.
     * @param fieldPath
     * @param value - usually this is the value that comes from an RIE component
     */
    updateField(fieldPath, value) {
        // Make sure we normalize RIE values, as they may be different from the object we want to store in the data model
        const formattedValue = this.getFormattedValue(value);
        _.set(this.process, fieldPath, formattedValue);
    }

    /**
     * Delete multiple entities from process (i.e., coordinate systems, variables, input datasets, output datastream)
     * @param listPath
     * @param selectedItems
     */
    deleteEntities(listPath, selectedItems) {
        //list passed in could be a map or array, letting this code work for both...
        const newList = _.get(this.process, listPath);
        let selectedItemsObject = {};

        if (Array.isArray(selectedItems)) {
            //delete entites needs
            selectedItems.forEach(id => {
                selectedItemsObject[id] = id;
            });
        } else {
            selectedItemsObject = selectedItems;
        }

        if (Array.isArray(newList)) {
            Object.keys(selectedItemsObject).forEach(itemId => {
                if (selectedItemsObject[itemId]) {
                    const index = newList.indexOf(itemId);
                    newList.splice(index, 1);
                }
            });

        } else {
            Object.keys(selectedItemsObject).forEach(itemId => {
                if (selectedItemsObject[itemId]) {
                    delete newList[itemId];
                }
            });
        }

        // Now we have to do dependency cleanup on the deleted items
        if (listPath === 'output_datastreams') {
            this.removeOutputDatastreamDependencies(selectedItemsObject);

        } else if (listPath === 'variable_retrieval.coordinate_systems') {
            this.removeCoordinateSystemDependencies(selectedItemsObject);

        } else if (listPath === 'variable_retrieval.retrieved_variables') {
            this.removeRetrievedVariableDependencies(selectedItemsObject);

        } else if (listPath === 'variable_retrieval.input_datasets') {
            this.removeInputDatasetDependencies(selectedItemsObject);
        }
    }

    removeInputDatasetDependencies(deletedInputDatasets) {
        const retrievedVariables = Object.keys(this.process.variable_retrieval.retrieved_variables);
        const variablesToDelete = {};
        const coordinateSystems = Object.keys(this.process.variable_retrieval.coordinate_systems);

        Object.keys(deletedInputDatasets).forEach(inputDatasetId => {
            // Delete all the variables belonging to this input dataset
            retrievedVariables.forEach(variableId => {
                const variable = this.process.variable_retrieval.retrieved_variables[variableId];
                if (variable.input_dataset === inputDatasetId) {
                    variablesToDelete[variableId] = true;
                }
            });

            // Remove any coordinate system mapping
            coordinateSystems.forEach(coordinateSystemId => {
                const coordinateSystem = this.process.variable_retrieval.coordinate_systems[coordinateSystemId];
                const dimensions = Object.keys(coordinateSystem.dimensions);
                dimensions.forEach(dimensionId => {
                    const dimension = coordinateSystem.dimensions[dimensionId];
                    if (dimension.gridding_method === 'mapping' && dimension.input_dataset === inputDatasetId) {
                        //TODO: maybe want to have this switch to a different possible input_dataset if one exists?
                        dimension.input_dataset = null;
                        dimension.coordinate_variables_per_rule = [];
                    }
                });
            });

        });

        // Clean up any linked variables
        if (Object.keys(variablesToDelete).length > 0) {
            this.deleteEntities('variable_retrieval.retrieved_variables', variablesToDelete);
        }

        // Re-sync the input datastreams list
        this.syncInputStreams();
    }

    removeRetrievedVariableDependencies(deletedRetrievedVariables) {

        const inputDatasets = Object.keys(this.process.variable_retrieval.input_datasets);
        const mappedDatastreams = Object.keys(this.process.variable_retrieval.output_datastream_variable_mappings);
        const inputDatasetsToDelete = {};

        // delete mappings from the retrieved variables that were deleted from list of input dataset and output datastream variable mappings
        Object.keys(deletedRetrievedVariables).forEach(variableId => {
            if (deletedRetrievedVariables[variableId]) {
                this.removeTransformParameterVariables(variableId);

                inputDatasets.forEach(inputDatasetId => {
                    let inputDataset = this.process.variable_retrieval.input_datasets[inputDatasetId];

                    inputDataset.rules.forEach(rule => {
                        if (rule.variable_mappings && Object.keys(rule.variable_mappings).indexOf(variableId) > -1) {
                            delete rule.variable_mappings[variableId];

                            // If there are no more variables belonging to this input dataset, then delete it
                            if (Object.keys(rule.variable_mappings).length === 0) {
                                inputDatasetsToDelete[inputDatasetId] = true;
                            }
                        }
                    });
                });

                mappedDatastreams.forEach(datastreamId => {
                    const datastreamMapping = this.process.variable_retrieval.output_datastream_variable_mappings[datastreamId];
                    const mappedVariables = Object.keys(datastreamMapping);
                    if (mappedVariables.indexOf(variableId) > -1) {
                        delete datastreamMapping[variableId];
                    }
                });
            }
        });

        if (Object.keys(inputDatasetsToDelete).length > 0) {
            this.deleteEntities('variable_retrieval.input_datasets', inputDatasetsToDelete);
        }

        this.syncCoordinateSystemDimensions();
    }

    removeCoordinateSystemDependencies(deletedCoordinateSystems) {
        const retrievedVars = Object.keys(this.process.variable_retrieval.retrieved_variables);
        const variableNames = [];

        // delete coordinate systems from retrieved variables
        Object.keys(deletedCoordinateSystems).forEach(coordSysId => {
            if (deletedCoordinateSystems[coordSysId]) {
                retrievedVars.forEach(variableId => {
                    const variable = this.process.variable_retrieval.retrieved_variables[variableId];
                    if (variable.coordinate_system && variable.coordinate_system === coordSysId) {
                        variableNames.push(variableId);
                    }
                });

                this.removeTransformationParameterCoordinateSystem(coordSysId);
            }
        });

        if (variableNames.length > 0) {
            this.addRetrievedVariablesToCoordinateSystem(null, variableNames);
        }
    }

    /**
     * After an output datastream is deleted, this will clean up any mappings from variables
     * @param deletedOutputDatastreams
     */
    removeOutputDatastreamDependencies(deletedOutputDatastreams) {
        const mappings = this.process.variable_retrieval.output_datastream_variable_mappings;

        // delete output datastreams from variable mappings list
        Object.keys(deletedOutputDatastreams).forEach(itemId => {
            if (deletedOutputDatastreams[itemId]) {

                if (mappings &&
                    mappings[itemId] &&
                    Object.keys(mappings[itemId]).length > 0) {
                    delete mappings[itemId];
                }

                if (this.process.output_interval[itemId]) {
                    delete this.process.output_interval[itemId];
                }
            }
        });
    }

    //TODO get wizard mode from state rather than pass in?
    renameProcess(newName, openTabOnRename, wizardMode) {
        // only set oldName on first renaming
        if (!this.process.oldName) {
            this.process.oldName = this.processId;
        }

        // if renamed back to original name, remove oldName prop
        if (this.process.oldName === newName) {
            delete this.process.oldName;
        }

        this.process.name = newName;

        // If the process was renamed, we have to switch the tab too
        this.postCommitCallbacks.push(() => {
            SecurityService.addWritableProcess(this.process.name);
            if (openTabOnRename) {
                let path = null;
                if (wizardMode) {
                    path = `/process/${this.process.name}/wizard`;
                }
                TabsService.renameTab(this.processId, this.process.name, path);
            }
        });
    }

    /**
     * Switch the process to use variable retrieval (instead of basic inputs - e.g., ingest)
     */
    addVariableRetrieval() {
        // If we had previously stashed a retrieval (from user clicking back and forth on the retrieval radio)
        // then pull it back out
        if (this.processUI.previous_retrieval) {
            this.process.variable_retrieval = this.processUI.previous_retrieval;
            delete this.processUI.previous_retrieval;

        } else {
            this.process.variable_retrieval = _.cloneDeep(Constants.VARIABLE_RETRIEVAL_NEW);
            
            // fill missing transform parameters
            this.fillMissingTransformParameters();
        }
    }

    /**
     * Add any transform parameters that are missing.  The UI expects all transform params to be initialized as empty objects.
     * @param process
     */
    fillMissingTransformParameters() {

        const addIfNotExists = (transformParameterName, fileType, process) => {
            if (!(transformParameterName in process.variable_retrieval.transformation_parameters)) {
                const param = _.cloneDeep(Constants.TRANSFORM_PARAMETER_NEW);
                param.name = transformParameterName;
                param.file_type = fileType;
                process.variable_retrieval.transformation_parameters[transformParameterName] = param;
            }
        };

        // Make sure all the transform parameters are available in the data structure to avoid NPE
        if (this.process.variable_retrieval && this.process.variable_retrieval.transformation_parameters) {
            addIfNotExists('transformation_type', 'coordinate_system', this.process);
            addIfNotExists('range', 'input_datastream', this.process);
            addIfNotExists('width', 'coordinate_system', this.process);
            addIfNotExists('missing_value', 'input_datastream', this.process);
            addIfNotExists('qc_mask', 'input_datastream', this.process);
            addIfNotExists('qc_bad', 'input_datastream', this.process);
            addIfNotExists('std_ind_max', 'coordinate_system', this.process);
            addIfNotExists('std_bad_max', 'coordinate_system', this.process);
            addIfNotExists('goodfrac_ind_min', 'coordinate_system', this.process);
            addIfNotExists('goodfrac_bad_min', 'coordinate_system', this.process);

            // We also have to make sure that the transformation_type parameter has coordinate system
            // defaults for all coordinate systems and dimensions in the this.process
            const coordSysDefaults = this.process.variable_retrieval.transformation_parameters.transformation_type.coordinate_system_defaults;
            Object.values(this.process.variable_retrieval.coordinate_systems).forEach(coordinateSystem => {
                Object.values(coordinateSystem.dimensions).forEach(dimension => {
                    if (!coordSysDefaults.find(x => x.name === coordinateSystem.name && x.dim === dimension.name)) {
                        if (dimension.data_transformation) {
                            coordSysDefaults.push(
                                {
                                    name: coordinateSystem.name,
                                    dim: dimension.name,
                                    default_value: dimension.data_transformation.transformation_type,
                                },
                            );
                        }
                    }
                });
            });
        }
    }

    /**
     * Switch back to basic inputs (no variable retrieval)
     */
    removeVariableRetrieval() {
        // Stash our existing retrieval so we can use it again if the user toggles back and forth on the retrival radio)
        this.processUI.previous_retrieval = _.cloneDeep(this.process.variable_retrieval);
        this.process.variable_retrieval = null;
    }

    fillEmptyTransformationParameters(exploredDatastreams) {
        if (!this.process.variable_retrieval.transformation_parameters) {
            this.process.variable_retrieval.transformation_parameters = {};
        }

        // add empty transformation parameters if they don't already exist
        Object.keys(Constants.TRANSFORMATION_PARAMETERS).forEach(parameterName => {
            const needsDimensions = ProcessUtils.parameterIsDimensionSpecific(parameterName);
            const retrievedVariables = _.get(this.process, ['variable_retrieval', 'retrieved_variables'], null);
            const coordinateSystems = _.get(this.process, ['variable_retrieval', 'coordinate_systems'], null);

            if (!needsDimensions && Object.keys(retrievedVariables).length > 0) {
                const defaultValue = this.getTransformationParameterDefaultValue(parameterName, null, null, null, null, exploredDatastreams);

                this.initializeTransformationParameter(parameterName, null, null, defaultValue, exploredDatastreams);
            }

            if (needsDimensions && Object.keys(coordinateSystems).length > 0) {
                ProcessUtils.visitDimensions(this.process, (dimension, coordinateSystemName) => {
                    const dimensionName = dimension.name;
                    const transformationTypeValue = this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName].data_transformation.transformation_type;

                    let defaultValue = this.getTransformationParameterDefaultValue(parameterName, coordinateSystemName, dimensionName, null, null, exploredDatastreams);
                    if (parameterName === 'transformation_type') {
                        defaultValue = transformationTypeValue;
                    }

                    this.initializeTransformationParameter(parameterName, coordinateSystemName, dimensionName, defaultValue, exploredDatastreams);
                });
            }
        });

    }


    /**
     * @param parameterName
     * @param coordinateSystemName
     * @param dimensionName
     * @param defaultValue
     * @param exploredDatastreams
     */
    initializeTransformationParameter(parameterName, coordinateSystemName, dimensionName, defaultValue, exploredDatastreams) {
        const parameterFileType = Constants.TRANSFORMATION_PARAMETERS[parameterName].file_type;

        let parameter = this.process.variable_retrieval.transformation_parameters[parameterName];
        if (!parameter) {
            parameter = {
                name: parameterName,
                file_type: parameterFileType,
                coordinate_system_defaults: [],
                input_datastream_defaults: [],
                variables: []
            };
            this.process.variable_retrieval.transformation_parameters[parameterName] = parameter;
        }

        const displayDefault = Constants.TRANSFORMATION_PARAMETERS[parameterName].display_default_init;
        const isInputParameter = ProcessUtils.parameterIsInputDatastreamDependent(parameterName);
        const isRange = parameterName === 'range';

        if (displayDefault && (!isInputParameter || isRange)) { // add the coordinate system default

            // Note; this only adds a default if one does not already exist for that dimension
            this.addTransformationCoordinateDefault([parameterName], coordinateSystemName, dimensionName, defaultValue);

        } else if (ProcessUtils.shouldDisplayTransformationOverride(this.process, [parameterName])) {

            // Note; this won't re-add the override if it already exists
            this.addTransformationParameterOverride([parameterName], coordinateSystemName, dimensionName, exploredDatastreams);
        }
    }

    removeTransformationParameterDimension(parameterName, coordinateSystemName, dimensionName) {

        this.process = ProcessUtils.filterTransformationCoordinateSystemDefaults(this.process, parameterName, (coordinateObj) => {
            const dimension = ProcessUtils.getDimension(this.process, coordinateSystemName, dimensionName);

            // If the parameterName is 'transformation_type' and the dimension still exists, the we DO NOT delete
            // this parameter since it should always remain in sync with the coordinate system.
            if (dimension && parameterName === 'transformation_type' && coordinateObj.name === coordinateSystemName && coordinateObj.dim === dimensionName) {
                return true;
            }
            return !(coordinateObj.name === coordinateSystemName && coordinateObj.dim === dimensionName);
        });

        this.process = ProcessUtils.filterTransformationVariables(this.process, parameterName, (variableObj) => {
            const variableCoordinateSystem = ProcessUtils.getTransformVariableCoordinateSystem(this.process, parameterName, variableObj.name);

            return !(variableCoordinateSystem === coordinateSystemName && variableObj.dim === dimensionName);
        });

    }

    addTransformationDefault = (parameterNames, coordinateSystemName, dimensionName) => {
        parameterNames.forEach(parameterName => {
            if (ProcessUtils.isInputQCParameter(parameterName)) {
                _.set(this.processUI, ['transformationParameterDefault', parameterName], true);
            } else {
                this.addTransformationCoordinateDefault([parameterName], coordinateSystemName, dimensionName);
            }
        });
    };

    /**
     * Add coordinate system defaults for the given transformation parameter.
     * @param parameterNames
     * @param coordinateSystemName
     * @param dimensionName
     * @param value
     */
    addTransformationCoordinateDefault = (parameterNames, coordinateSystemName, dimensionName, value) => {
        parameterNames.forEach(parameterName => {
            let defaultValue = value;
            if (!defaultValue) {
                defaultValue = this.getTransformationParameterDefaultValue(parameterName, coordinateSystemName, dimensionName);
            }
            const coordinateSystem = {
                name: coordinateSystemName,
                dim: dimensionName,
                default_value: defaultValue
            };

            let addDefault = true;

            ProcessUtils.visitTransformationCoordinateSystemDefaults(this.process, parameterName, (coordinateObj) => {
                if (coordinateObj.name === coordinateSystemName && coordinateObj.dim === dimensionName) {
                    addDefault = false;

                    // If this is is the transform_type parameter, always override the default value.
                    // We need to do this because the transformation type default ALWAYS gets reset to match the coordinate system value.
                    // TODO: this is confusing and needs to be refactored
                    if (parameterName === 'transformation_type') {
                        coordinateObj.default_value = defaultValue;
                    }
                }
            });

            if (addDefault) {
                this.process.variable_retrieval.transformation_parameters[parameterName].coordinate_system_defaults.push(coordinateSystem);
            }
        });
    };

    addTransformationInputDatastreamDefault = (parameterNames, datastreamName, value = null) => {
        parameterNames.forEach(parameterName => {
            let defaultValue = value ? value : Constants.TRANSFORMATION_PARAMETERS[parameterName].default_value;

            // input datastream defaults all use time dimension
            const inputDatastreamDefault = {
                name: datastreamName,
                default_value: defaultValue,
                dim: 'time'
            };
            this.process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults.push(inputDatastreamDefault);
        });
    };

    removeTransformationInputDatastreamDefault = (parameterNames, datastreamNames) => {
        parameterNames.forEach(parameterName => {
            this.process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults = this.process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults.filter(defaultObj => {
                return datastreamNames.indexOf(defaultObj.name) === -1;
            });
        });
    };

    updateTransformationInputDatastreamDefault = (parameterNames, datastreamName, value) => {
        parameterNames.forEach(parameterName => {
            this.process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults = this.process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults.map(defaultObj => {
                if (defaultObj.name === datastreamName) {
                    defaultObj.default_value = value;
                }
                return defaultObj;
            });
        });
    };

    removeTransformationParameterDefault = (parameterNames, coordinateSystemName, dimensionName) => {
        parameterNames.forEach(parameterName => {
            this.process = ProcessUtils.filterTransformationCoordinateSystemDefaults(this.process, parameterName, (coordinateObj) => {
                return !(coordinateObj.name === coordinateSystemName && coordinateObj.dim === dimensionName);
            });
        });
    };

    updateTransformationParameterDefault = (parameterName, coordinateSystemName = null, dimensionName = null, value) => {
        if (coordinateSystemName !== null && dimensionName !== null) {
            this.process = ProcessUtils.mapTransformationCoordinateSystemDefaults(this.process, parameterName, (coordinateObj) => {
                if (coordinateObj.name === coordinateSystemName && coordinateObj.dim === dimensionName) {
                    coordinateObj.default_value = value;
                }
                return coordinateObj;
            });
        } else if (ProcessUtils.isInputQCParameter(parameterName)) {

        }
    };

    /**
     * @param parameterNames
     */
    addTransformationParameterDatastreamDefault = (parameterNames) => {
        parameterNames.forEach(parameterName => {
            _.set(this.processUI, ['transformationParameterDatastreamDefault', parameterName], true);
        });
    };

    removeTransformationParameterDatastreamDefault = (parameterNames, datastreamName) => {
        parameterNames.forEach(parameterName => {
            this.process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults = [];
            _.set(this.processUI, ['transformationParameterDatastreamDefault', parameterName], false);

        });
    };

    /**
     * @param parameterNames
     * @param coordinateSystemName
     * @param dimensionName
     */
    addTransformationParameterOverride = (parameterNames, coordinateSystemName, dimensionName) => {
        parameterNames.forEach(parameterName => {
            // Add to the UI state that we want to override the parameter by variable (in case there is no default data to make it
            // obvious whether to show the override table or not.
            //TODO: this should be coord/dim specific
            _.set(this.processUI, ['transformationParameterOverride', parameterName], true);
        });
    };

    /**
     * Only add if the transform parameter variable is not already in the list for that dimension
     * @param parameterName
     * @param dimensionName
     * @param inputDatastream
     * @param variableName
     * @param value
     */
    addTransformationParameterOverrideVariable = (parameterName, dimensionName, inputDatastream, variableName, value) => {
        let match = false;
        this.process.variable_retrieval.transformation_parameters[parameterName].variables.forEach(variableObj => {
            if (variableObj.dim === dimensionName && variableObj.name === variableName) {
                match = true;
            }
        });

        if (!match) {
            if (parameterName === 'qc_mask' || parameterName === 'qc_bad') {
                // convert from string to number
                if (value === null) {
                    value = [];
                }
                value = value.map(v => { return Number(v); });
            }
            this.process.variable_retrieval.transformation_parameters[parameterName].variables.push({
                name: variableName,
                value,
                dim: dimensionName ? dimensionName : null,
                input_datastream: inputDatastream ? inputDatastream : null
            });
        }
    };

    getTransformationParameterDefaultValue = (parameterName, coordinateSystemName, dimensionName, retrievedVariableName, inputDatastreamName, exploredDatastreams) => {
        let value = Constants.TRANSFORMATION_PARAMETERS[parameterName].default_value;
        const ttype = ProcessUtils.getDimensionTransformationType(this.process, coordinateSystemName, dimensionName);

        switch (parameterName) {
            case 'width':
                if (ttype === Constants.TRANSFORM_TYPE_SUBSAMPLE) {
                    // If transformation type is nearest neighbor, we need to default bin width to 0
                    value = 0;

                } else {
                    // Else (transformation type is average, auto, or interpolate), we need to use the interval size as the default width
                    value = ProcessUtils.getDimensionInterval(this.process, coordinateSystemName, dimensionName, exploredDatastreams);
                }
                break;
            case 'std_ind_max':
            case 'std_bad_max':
            case 'goodfrac_ind_min':
            case 'goodfrac_bad_min':
                ProcessUtils.visitTransformationCoordinateSystemDefaults(this.process, parameterName, (coordinateObj) => {
                    if (coordinateObj.name === coordinateSystemName && coordinateObj.dim === dimensionName) {
                        value = coordinateObj.default_value;
                    }
                });
                break;
            case 'qc_mask':
            case 'qc_bad':
                if (!exploredDatastreams) {
                    exploredDatastreams = DataExplorerService.getExploredDatastreams(this.process.name);
                }
                if (exploredDatastreams && inputDatastreamName && retrievedVariableName) {
                    const qcType = parameterName === 'qc_mask' ? 'bit' : 'integer';
                    value = ProcessUtils.getQCBits(this.process, inputDatastreamName, retrievedVariableName, 'bad', qcType);
                }
            default:
                break;
        }

        return value;
    };

    updateTransformationParameterOverride = (parameterName, coordinateSystemName = null, dimensionName = null, datastreamName = null, variableName, value) => {
        // Make sure the variable override exists (won't add if it already exists)
        this.addTransformationParameterVariable([parameterName], coordinateSystemName, dimensionName, variableName);

        ProcessUtils.mapTransformationVariables(this.process, parameterName, (variableObj) => {
            const variableCoordinateSystem = ProcessUtils.getTransformVariableCoordinateSystem(this.process, parameterName, variableObj.name);

            if (variableCoordinateSystem === coordinateSystemName && variableObj.dim === dimensionName
                && variableObj.input_datastream === datastreamName && variableObj.name === variableName) {
                variableObj.value = value;
            }
            return variableObj;
        });
    };

    removeTransformationParameterOverride = (parameterNames, coordinateSystemName = null, dimensionName = null) => {
        parameterNames.forEach(parameterName => {

            // Remove all the overridden variables for this transform parameter
            this.process = ProcessUtils.filterTransformationVariables(this.process, parameterName, (variableObj) => {
                const variableCoordinateSystem = ProcessUtils.getTransformVariableCoordinateSystem(this.process, parameterName, variableObj.name);
                return !(variableCoordinateSystem === coordinateSystemName && variableObj.dim === dimensionName);
            });

            // remove explored data variables
            if (ProcessUtils.parameterIsExploredDataDependent(parameterName)) {
                this.process.variable_retrieval.transformation_parameters[parameterName].variables = [];
            }

            // Tell the UI state not to show the transformation parameter override table anymore
            _.set(this.processUI, ['transformationParameterOverride', parameterName], false);
        });
    };

    addTransformationParameterVariable = (parameterNames, coordinateSystemName, dimensionName, variableName, inputDatastream) => {
        parameterNames.forEach(parameterName => {
            const value = this.getTransformationParameterDefaultValue(parameterName, coordinateSystemName, dimensionName, variableName, inputDatastream);
            this.addTransformationParameterOverrideVariable(parameterName, dimensionName, inputDatastream, variableName, value);
        });
    };

    removeTransformationParameterVariable = (parameterNames, coordinateSystemName, dimensionName, variableName, datastreamName) => {
        parameterNames.forEach(parameterName => {
            ProcessUtils.filterTransformationVariables(this.process, parameterName, (variableObj) => {
                if (dimensionName) {
                    const variableCoordinateSystem = ProcessUtils.getTransformVariableCoordinateSystem(this.process, parameterName, variableObj.name);

                    return !(variableCoordinateSystem === coordinateSystemName && variableObj.dim === dimensionName
                    && variableObj.name === variableName && variableObj.input_datastream === datastreamName);
                } else {
                    return !(variableObj.name === variableName && variableObj.input_datastream === datastreamName);
                }
            });
        });
    };

    updateTransformationTypeDependentParameter(parameterName, coordinateSystemName, dimensionName, exploredDatastreams) {

        // We never remove transformation_type because we need that to always be in sync with the coord system dimension
        if (parameterName === 'transformation_type') {
            // TODO: do we want to remove transformation_type variable overrides and bin_alignment if
            // the transformation_type is passthrough?  It doesn't hurt to have the extra parameters saved in the db
            // because if ttype is passthrough, they will just be ignored.  Pros: if we clean this up, it will
            // make the process less confusing (having parameters set that don't mean anything is confusing)
            // Cons: If user toggles back and forth in the UI, they will lose all the previous settings.
            return;
        }

        // Decide if the transformation type warrants removal or addition of the transform parameter
        if (ProcessUtils.isRelevantTransformationParameter(this.process, parameterName, [coordinateSystemName], dimensionName, exploredDatastreams)) {
            // This will add the transform parameter if it doesn't already exist.
            // TODO: it was super confusing whether the default value was getting changed or not, but I think I modified
            // the code so that if the parameter default value already exists, it will not get reset, except in the case where
            // the parameter is transformation_type
            const defaultValue = this.getTransformationParameterDefaultValue(parameterName, coordinateSystemName, dimensionName);
            this.initializeTransformationParameter(parameterName, coordinateSystemName, dimensionName, defaultValue, exploredDatastreams);

        } else {
            this.removeTransformationParameterDimension(parameterName, coordinateSystemName, dimensionName, exploredDatastreams);
        }

        if (ProcessUtils.parameterIsInputDatastreamDependent(parameterName) && parameterName !== 'range') {
            // We also have to filter out any input datastream override variables that have a passthrough dimension since input
            // qc will not be used if the transformation is passthrough

            // need to have all datastreams explored before filtering or else saved values will be removed
            let dataReady = true;
            this.process.input_datastreams.forEach(datastream => {
                if (!exploredDatastreams || !exploredDatastreams[datastream] || _.isEmpty(exploredDatastreams[datastream].currentData)) {
                    dataReady = false;
                }
            });

            if (dataReady) {
                this.process = ProcessUtils.filterTransformationVariables(this.process, parameterName, (variableObj) => {
                    return ProcessUtils.isRelevantInputDatastreamOverrideVariable(this.process, parameterName, variableObj.name, exploredDatastreams);
                });
            }
        }

    }

    /**
     * Check all the dimensions to see if one has an old trange property set.  If so, then convert this over to a
     * coordinate system default for the range transformation parameter.
     */
    convertTrange() {
        ProcessUtils.visitDimensions(this.process, (dimension, coordinateSystemName) => {
            if (dimension.trange) {
                this.updateTransformationParameterDefault('range', coordinateSystemName, dimension.name, dimension.trange);
                delete dimension.trange;
            }
        });
    }

    /**
     * Some transform parameters only apply if the transformation type is average or interpolate.  So when the
     * transformation type changes either for the whole coordinate system or for a specific variable, we
     * need to get rid of any other transform parameters that no longer apply.
     * @param coordinateSystemName string
     * @param inputDatastreamName string
     * @param transforamtionTypeValue string
     */
    updateTransformationTypeDependentParameters(coordinateSystemName, dimensionName, exploredDatastreams) {
        if (!exploredDatastreams || _.isEmpty(exploredDatastreams)) {
            exploredDatastreams = DataExplorerService.getExploredDatastreams(this.processId);
        }

        Object.keys(Constants.TRANSFORMATION_PARAMETERS).forEach(parameterName => {
            this.updateTransformationTypeDependentParameter(parameterName, coordinateSystemName, dimensionName, exploredDatastreams);
        });
    }

    renameTransformParameterCoordinateSystem = (oldName, newName) => {
        ProcessUtils.visitTransformationParameters(this.process, (parameter) => {
            const parameterName = parameter.name;

            this.process = ProcessUtils.mapTransformationCoordinateSystemDefaults(this.process, parameterName, (coordinateObj) => {
                if (coordinateObj.name === oldName) {
                    coordinateObj.name = newName;
                }
                return coordinateObj;
            });
        });
    };

    renameTransformParameterDimension = (coordinateSystemName, oldDimensionName, newDimensionName) => {
        ProcessUtils.visitTransformationParameters(this.process, (parameter) => {
            const parameterName = parameter.name;
            this.process = ProcessUtils.mapTransformationCoordinateSystemDefaults(this.process, parameterName, (coordinateObj) => {
                if (coordinateObj.name === coordinateSystemName && coordinateObj.dim === oldDimensionName) {
                    coordinateObj.dim = newDimensionName;
                }
                return coordinateObj;
            });

            this.process = ProcessUtils.mapTransformationVariables(this.process, parameterName, (variableObj) => {
                const variableCoordinateSystem = ProcessUtils.getTransformVariableCoordinateSystem(this.process, parameterName, variableObj.name);

                if (variableCoordinateSystem === coordinateSystemName && variableObj.dim === oldDimensionName) {
                    variableObj.dim = newDimensionName;
                }
                return variableObj;
            });
        });
    };

    removeTransformationParameterCoordinateSystem = (coordinateSystemName) => {
        ProcessUtils.visitTransformationParameters(this.process, (parameter) => {
            const parameterName = parameter.name;
            this.process = ProcessUtils.filterTransformationCoordinateSystemDefaults(this.process, parameterName, (coordinateObj) => {
                return coordinateObj.name !== coordinateSystemName;
            });

            this.process = ProcessUtils.filterTransformationVariables(this.process, parameterName, (variableObj) => {
                const variableCoordinateSystem = ProcessUtils.getTransformVariableCoordinateSystem(this.process, parameterName, variableObj.name);
                return variableCoordinateSystem !== coordinateSystemName;
            });
        });
    };

    removeTransformParameterVariables = (variableName) => {
        ProcessUtils.visitTransformationParameters(this.process, (parameter) => {
            //I'm not sure how an undefined case happens but it did when deleting multiple retrieved variables from list view
            if (parameter) {
                const parameterName = parameter.name;

                this.process = ProcessUtils.filterTransformationVariables(this.process, parameterName, (variableObj) => {
                    return variableObj.name !== variableName;
                });
            }
        });
    };

    updateTransformOverrideForVariables = (parameterName, variableList, addVariables) => {
        if (this.process.variable_retrieval.transformation_parameters) {
            variableList.forEach(variableName => {
                const retrievedVariable = this.process.variable_retrieval.retrieved_variables[variableName];
                const coordinateSystemName = retrievedVariable.coordinate_system;

                retrievedVariable.dims.forEach(dimensionName => {
                    if (addVariables) {
                        this.addTransformationParameterVariable([parameterName], coordinateSystemName, dimensionName, variableName, null);
                    } else {
                        this.removeTransformationParameterVariable([parameterName], coordinateSystemName, dimensionName, variableName, null);
                    }
                });
            });
        }
    };

    updateTransformOverrideValueForVariables = (parameterName, variableList, coordinateSystemName, dimensionName, value) => {
        value = this.getFormattedValue(value);
        variableList.forEach(variableName => {
            if (ProcessUtils.getRetrievedVariableDimensions(this.process, variableName).indexOf(dimensionName) > -1 &&
                this.process.variable_retrieval.retrieved_variables[variableName].coordinate_system === coordinateSystemName) {

                this.updateTransformationParameterOverride(parameterName, coordinateSystemName, dimensionName, null, variableName, value);
            }
        });
    };

    /**
     *
     * @param oldCoordinateSystemName string
     * @param newCoordinateSystemName string
     * @param navigate boolean - whether to navigate after renaming coordinate system name
     * @param dimensionName string - not required, only if navigate is true, specify the dimension page to navigate to
     */
    renameCoordinateSystem(oldCoordinateSystemName, newCoordinateSystemName, navigate, dimensionName) {

        // Update the coordinate system object
        const coordSys = this.process.variable_retrieval.coordinate_systems[oldCoordinateSystemName];
        coordSys.name = newCoordinateSystemName;
        this.process.variable_retrieval.coordinate_systems[newCoordinateSystemName] = coordSys;
        delete this.process.variable_retrieval.coordinate_systems[oldCoordinateSystemName];

        // Now update all retrieved variables that use that coordinate system
        const retrievedVariables = Object.keys(this.process.variable_retrieval.retrieved_variables);
        retrievedVariables.forEach(retrievedVariableId => {
            const retrievedVariable = this.process.variable_retrieval.retrieved_variables[retrievedVariableId];
            if (retrievedVariable.coordinate_system === oldCoordinateSystemName) {
                retrievedVariable.coordinate_system = newCoordinateSystemName;
            }
        });

        this.renameTransformParameterCoordinateSystem(oldCoordinateSystemName, newCoordinateSystemName);

        if (navigate) {
            this.postCommitCallbacks.push(() => {
                NavigationUtils.gotoCoordinateSystem(this.process.name, null, newCoordinateSystemName, dimensionName);
            });
        }
    }

    addCoordinateSystem(coordinateSystemName) {
        const newCoordSys = _.cloneDeep(Constants.COORDINATE_SYSTEM_NEW); // time dimension added by default
        newCoordSys.name = coordinateSystemName;
        this.process.variable_retrieval.coordinate_systems[coordinateSystemName] = newCoordSys;

        this.initializeDimension(coordinateSystemName, 'time');
    }

    setCoordinateSystemForVariable(coordinateSystemName, variableName) {
        const formattedValue = this.getFormattedValue(coordinateSystemName);
        _.set(this.process, `variable_retrieval.retrieved_variables.${variableName}.coordinate_system`, formattedValue);
    }

    renameVariableDimension(retrievedVariableName, oldDimensionName, newDimensionName) {
        const retrievedVariable = this.process.variable_retrieval.retrieved_variables[retrievedVariableName];
        if (retrievedVariable) {
            for (let i = 0; i < retrievedVariable.dims.length; i++) {
                if (retrievedVariable.dims[i] === oldDimensionName) {
                    retrievedVariable.dims[i] = newDimensionName;
                }
            }
        }
    }

    renameDimension(coordinateSystemName, oldDimensionName, newDimensionName, navigate) {
        const dimension = this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[oldDimensionName];
        dimension.name = newDimensionName;

        //if new dimension name already exists, keep original values
        if (!this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[newDimensionName]) {
            this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[newDimensionName] = dimension;
        }
        delete this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[oldDimensionName];

        //update index values in dimension objects
        const dimsArray = Object.values(this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions).map(dim => {
            return dim;
        });
        const newDimensions = this.updateDimensionsIndices(dimsArray);
        this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions = newDimensions;

        // Iterate through all variables, and if the variable uses this coordinate system and has a dimension of the same name,
        // then we have to change the corresponding dim field on the variable to the new name.
        // Now update all retrieved variables that use that coordinate system
        const retrievedVariables = Object.values(this.process.variable_retrieval.retrieved_variables);
        retrievedVariables.forEach(retrievedVariable => {
            if (retrievedVariable.coordinate_system === coordinateSystemName) {
                this.renameVariableDimension(retrievedVariable.name, oldDimensionName, newDimensionName);
            }
        });

        this.renameTransformParameterDimension(coordinateSystemName, oldDimensionName, newDimensionName);

        if (navigate) {
            this.postCommitCallbacks.push(() => {
                NavigationUtils.gotoCoordinateSystem(this.process.name, null, coordinateSystemName, newDimensionName);
            });
        }
    }

    /**
     * @param coordinateSystemName
     * @param dimensionName
     * @param value
     * @returns {*}
     */
    updateGriddingMethod(coordinateSystemName, dimensionName, value) {
        const griddingMethod = this.getFormattedValue(value);
        const dimension = this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName];
        dimension.gridding_method = griddingMethod;

        if (griddingMethod === 'mapping') {
            // make sure the mapping parameters exist
            _.defaults(dimension, {
                input_dataset: null,
                coordinate_variables_per_rule: [[dimension.name]],
            });
            // If using mapping, then we need to decide if the dimension should use AUTO or PASSTHROUGH
            this.setDefaultDimensionTransformationType(coordinateSystemName, dimensionName);

        } else if (griddingMethod === 'regular_grid') {
            // make sure the regular grid parameters exist
            let end = 0;
            if (dimensionName === 'time' && this.process.processing_interval) {
                end = parseInt(this.process.processing_interval, 10);
            }

            dimension.data_transformation = Constants.DIMENSION_DEFAULT_TRANSFORMATION;
            dimension.data_transformation.end = end;
        }
    }

    updateDimensionInputDataset(coordinateSystemName, dimensionName, inputDatasetName) {
        const dimensionInputDatasetPath = ['variable_retrieval', 'coordinate_systems', coordinateSystemName, 'dimensions', dimensionName, 'input_dataset'];
        this.updateField(dimensionInputDatasetPath, inputDatasetName);

        this.setCoordinateVariablesPerRule(coordinateSystemName, dimensionName, inputDatasetName, null);
    }

    /**
     * Adds a dimension to the given coordinate system if it does not already exist
     * @param {*} coordinateSystemName 
     * @param {*} dimensionName 
     */
    addDimension(coordinateSystemName, dimensionName) {
        const dimensions = this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions;

        if (!dimensions[dimensionName]) {
            const inputDatasetsWithSameDimension = ProcessUtils.getInputDatasetsForDimension(this.process, dimensionName);

            let newDimension = null;

            if (dimensionName === 'time' || inputDatasetsWithSameDimension.length > 1) {
                newDimension = _.cloneDeep(Constants.DIMENSION_NEW_TIME);

            } else {
                newDimension = _.cloneDeep(Constants.DIMENSION_NEW_OTHER);
            }

            newDimension.coordinate_variables_per_rule[0] = [dimensionName];
            newDimension.name = dimensionName;
            let index = 0;

            if (dimensionName !== 'time') {
                index = Object.keys(dimensions).length;
            } else {
                // If the dimension is 'time' then it must be inserted as the first dimension
                // We are inserting the new dimension at 0, so we have to increment the other dimensions by 1
                Object.keys(dimensions).forEach(dimName => {
                    const dimension = dimensions[dimName];
                    dimension.index = dimension.index + 1;
                });
            }
            newDimension.index = index;
            this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName] = newDimension;
        }
    }

    /**
     * Determine what the default transformation type should be for the dimension based upon the current settings.
     * Note that by 'default' we mean the transformation type for the whole dimension, not any overriden values set
     * in transformation parameters.
     * This method is called every time the user changes data in the wizard to correct incorrect transformation types
     * that may no longer apply. This is called from in the wizard, where we are making all the decisions for the user.
     * This logic may not apply to regular pcm.
     *
     * TODO: we probably need to rename this method for clarity.  Maybe "correctDimensionTransformationType" would be better?
     * @param coordinateSystemName
     * @param dimensionName
     */
    setDefaultDimensionTransformationType(coordinateSystemName, dimensionName) {

        const dimension = this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName];
        const griddingMethod = dimension.gridding_method;

        // This logic only applies if the dimension coordinate variable is being mapped from a datastream
        if (griddingMethod === 'mapping') {
            const inputDatasetsWithSameDimension = ProcessUtils.getInputDatasetsForDimension(this.process, dimensionName);

            if (inputDatasetsWithSameDimension.length > 1) {
                // Only change it if it is currently passthrough, so we don't override values
                if (dimension.data_transformation.transformation_type === Constants.TRANSFORM_TYPE_PASSTHROUGH) {
                    dimension.data_transformation = Constants.DIMENSION_DEFAULT_TRANSFORMATION;
                }

            } else {
                dimension.data_transformation = {
                    transformation_type: Constants.TRANSFORM_TYPE_PASSTHROUGH
                };
            }
        }
    }

    removeDimension(coordinateSystemName, dimensionToDelete) {
        // Reorder the remaining dimensions

        const dimsArray = [];
        let indexToDelete = 0;
        Object.values(this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions).forEach(dimension => {
            dimsArray[dimension.index] = dimension;
            if (dimension.name === dimensionToDelete) {
                indexToDelete = dimension.index;
            }
        });
        dimsArray.splice(indexToDelete, 1);

        const newDimensions = this.updateDimensionsIndices(dimsArray);
        this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions = newDimensions;

        ProcessUtils.visitTransformationParameters(this.process, (parameter) => {
            this.removeTransformationParameterDimension(parameter.name, coordinateSystemName, dimensionToDelete);
        });
    }

    updateDimensionsIndices(dimsArray) {
        const newDimensions = {};

        for (let i = 0; i < dimsArray.length; i++) {
            const dimension = dimsArray[i];
            dimension.index = i;
            newDimensions[dimension.name] = dimension;
        }

        return newDimensions;
    }

    /**
     * Take the given process and make sure that all the input streams defined by retrieved variable
     * input dataset rules are included in the process.input_datastreams field.
     * @param processCopy
     */
    syncInputStreams() {
        if (this.process.variable_retrieval) {
            const inputDatastreams = [];
            ProcessUtils.visitAllInputDatasetRules(this.process, (rule) => {
                const ruleDatastream = rule.datastream_name;
                if (inputDatastreams.indexOf(ruleDatastream) === -1) {
                    inputDatastreams.push(ruleDatastream);
                }
            });
            this.process.input_datastreams = inputDatastreams;
        }
    }

    /**
     * Remove any dimensions from coordinate system that are no longer being used by any retrieved variable
     */
    syncCoordinateSystemDimensions() {
        if (Object.keys(this.process.variable_retrieval.coordinate_systems).length > 0) {
            const oldCoordinateDimensions = {};
            const currentCoordinateDimensions = {};
            Object.keys(this.process.variable_retrieval.coordinate_systems).forEach(coordinateSystemName => {
                oldCoordinateDimensions[coordinateSystemName] = Object.keys(this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions);
                currentCoordinateDimensions[coordinateSystemName] = [];
            });
            ProcessUtils.visitRetrievedVariables(this.process, retrievedVariable => {
                if (retrievedVariable.coordinate_system) {
                    currentCoordinateDimensions[retrievedVariable.coordinate_system] = [...new Set([...currentCoordinateDimensions[retrievedVariable.coordinate_system], ...retrievedVariable.dims])];
                }
            });

            Object.keys(oldCoordinateDimensions).forEach(coordinateSystemName => {
                const diff = _.difference(oldCoordinateDimensions[coordinateSystemName], currentCoordinateDimensions[coordinateSystemName]);
                if (diff.length > 0) {
                    diff.forEach(dimensionName => {
                        //even if time isn't being used we don't delete that one
                        if (dimensionName !== 'time') {
                            this.removeDimension(coordinateSystemName, dimensionName);
                        }
                    });
                }
            });
        }
    }

    renameInputDataset(oldName, newName) {
        // Update the input dataset object
        const inputDataset = this.process.variable_retrieval.input_datasets[oldName];
        inputDataset.name = newName;
        this.process.variable_retrieval.input_datasets[newName] = inputDataset;
        delete this.process.variable_retrieval.input_datasets[oldName];

        // Now update all retrieved variables that use that input dataset
        const retrievedVariables = Object.keys(this.process.variable_retrieval.retrieved_variables);
        retrievedVariables.forEach(retrievedVariableId => {
            const retrievedVariable = this.process.variable_retrieval.retrieved_variables[retrievedVariableId];
            if (retrievedVariable.input_dataset === oldName) {
                retrievedVariable.input_dataset = newName;
            }
        });

        // Now update any coordinate systems that are mapped to that input dataset
        ProcessUtils.visitDimensions(this.process, (dimension) => {
            if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING &&
                dimension.input_dataset === oldName) {
                dimension.input_dataset = newName;
            }
        });
    }

    renameOutputDatastream(oldName, newName, navigate) {
        // maintain position for process wizard
        this.process.output_datastreams.splice(this.process.output_datastreams.indexOf(oldName), 1, newName);
        const oldVariableMappings = this.process.variable_retrieval.output_datastream_variable_mappings[oldName];
        if (oldVariableMappings) {
            // move the variable mappings to the new name
            this.process.variable_retrieval.output_datastream_variable_mappings[newName] = oldVariableMappings;
        }
        delete this.process.variable_retrieval.output_datastream_variable_mappings[oldName];

        const oldIntervalValues = this.process.output_interval[oldName];
        this.process.output_interval[newName] = oldIntervalValues;
        delete this.process.output_interval[oldName];

        if (navigate) {
            this.postCommitCallbacks.push(() => {
                NavigationUtils.gotoOutputDatastream(this.process.name, null, newName);
            });
        }
    }

    addOutputDatastream(outputDatastreamName) {
        // only add it if it doesn't already exist
        if (!_.includes(this.process.output_datastreams, outputDatastreamName)) {
            this.process.output_datastreams.push(outputDatastreamName);
            this.process.variable_retrieval.output_datastream_variable_mappings[outputDatastreamName] = {};
            this.process.output_interval[outputDatastreamName] = { interval: null, timezone: null };
        }
    }

    /**
     * Create a new dataset and create one rule for each datastream in the list
     * @param inputDatasetName
     * @param datastreamNames - array of datastream names
     */
    createInputDataset(inputDatasetName, datastreamNames) {
        const offsets = [0, 0];
        for (let i = 0; i < datastreamNames.length; i++) {
            const datastreamName = datastreamNames[i];
            const rule = _.cloneDeep(Constants.INPUT_DATASET_RULE_NEW);
            rule.priority = i + 1;
            rule.datastream_name = datastreamName;
            this.editInputDataset(inputDatasetName, offsets, rule);
        }
    }

    /**
     * Set the output datastreams mapped to this variable.  Resets any previous value
     * @param {string} variableName
     * @param {string[]} outputDatastreams
     */
    setVariableOutputDatastreams(variableName, outputDatastreams) {
        const outputDatastreamMappings = this.process.variable_retrieval.output_datastream_variable_mappings;
        const outputDatastreamsWithExistingMappings = [];

        // First remove any mappings for datastreams that have been removed from the variable
        Object.keys(outputDatastreamMappings).forEach(datastreamName => {
            Object.keys(outputDatastreamMappings[datastreamName]).forEach(mappedVariableName => {
                if (mappedVariableName === variableName) {
                    if (!_.includes(outputDatastreams, datastreamName)) {
                        // This datastream has been removed from the variable, so we should delete the mappings
                        delete outputDatastreamMappings[datastreamName][variableName];
                    } else {
                        outputDatastreamsWithExistingMappings.push(datastreamName);
                    }
                }
            });
        });

        // Now add the mappings to the new output datastreams
        outputDatastreams.forEach(datastreamName => {
            this.addOutputDatastream(datastreamName); // make sure the datastream has been added

            // Only add a mapping if one doesn't already exist for that variable
            if (!_.includes(outputDatastreamsWithExistingMappings, datastreamName)) {

                if (!this.process.variable_retrieval.output_datastream_variable_mappings[datastreamName]) {
                    // make sure the object exists if this is the first time being assigned
                    this.process.variable_retrieval.output_datastream_variable_mappings[datastreamName] = {};
                }

                const datastreamVariableName = ProcessUtils.guessOutputDatastreamVariableName(this.process, datastreamName, variableName);
                this.process.variable_retrieval.output_datastream_variable_mappings[datastreamName][variableName] = datastreamVariableName;
            }
        });
    }

    /**
     * Associates the given retrieved variable with the given datastream.  Does not remove any existing mappings.
     * @param outputDatastream
     * @param retrievedVarName
     */
    addOutputDatastreamToVariable(outputDatastream, retrievedVarName) {

        const datastreamVariableName = ProcessUtils.guessOutputDatastreamVariableName(this.process, outputDatastream, retrievedVarName);
        const outputDatastreamsList = this.process.variable_retrieval.output_datastream_variable_mappings;
        outputDatastreamsList[outputDatastream] = Object.assign({}, outputDatastreamsList[outputDatastream], {
            [retrievedVarName]: datastreamVariableName
        });

    }

    /**
     * Updates value of output mapping
     * @param retrievedVariableName string
     * @param outputDatastreamName string
     * @param value string
     */
    setOutputDatastreamMappingForVariable(retrievedVariableName, outputDatastreamName, value) {
        this.process.variable_retrieval.output_datastream_variable_mappings[outputDatastreamName][retrievedVariableName] = value;
    }

    /**
     * Assigns the variable to the given input dataset.  If the variable was previously assigned to another dataset,
     * it reassigns the variable mappings.
     * @param inputDatasetName
     * @param retrievedVarName
     */
    setInputDatasetForVariable(inputDatasetName, retrievedVarName) {
        const oldInputDatasetName = this.process.variable_retrieval.retrieved_variables[retrievedVarName].input_dataset;
        const oldInputDataset = this.process.variable_retrieval.input_datasets[oldInputDatasetName];
        const newInputDataset = this.process.variable_retrieval.input_datasets[inputDatasetName];

        this.process.variable_retrieval.retrieved_variables[retrievedVarName].input_dataset = inputDatasetName;

        newInputDataset.rules.forEach((ruleNew) => {
            // Since the new dataset may have different number of rules and different datastreams, we loop
            // through all the old rules and if any have the same datastream name, then we copy the mappings over.
            oldInputDataset.rules.forEach((ruleOld) => {
                if (ruleNew.datastream_name == ruleOld.datastream_name) {
                    ruleNew.variable_mappings[retrievedVarName] = ruleOld.variable_mappings[retrievedVarName];
                }
            });
        });

        oldInputDataset.rules.forEach(rule => {
            delete rule.variable_mappings[retrievedVarName];
        });

    }

    /**
     * Adds a new input dataset if it does not already exist, else edits the existing dataset
     * @param inputDatasetName
     * @param offsets
     * @param rule
     */
    editInputDataset(inputDatasetName, offsets, rule) {
        let inputDataset = this.process.variable_retrieval.input_datasets[inputDatasetName];

        if (!inputDataset) {
            inputDataset = _.cloneDeep(Constants.INPUT_DATASET_NEW);
            inputDataset.name = inputDatasetName;
            this.process.variable_retrieval.input_datasets[inputDatasetName] = inputDataset;
        }
        // Update offsets if they are passed in
        if (offsets) {
            // make sure there is a value set for each
            if (!offsets[0]) {
                offsets[0] = 0;
            }
            if (!offsets[1]) {
                offsets[1] = 0;
            }

            inputDataset.offsets = offsets;
        }

        // Update rules, apending the new rule to the end
        if (rule) {
            inputDataset.rules.push(rule);
            rule.priority = inputDataset.rules.length;

            // Now we need to initialize the variable mappings for all the variables that belong to this input dataset
            ProcessUtils.visitRetrievedVariables(this.process, (retrievedVariable) => {
                if (retrievedVariable.input_dataset === inputDatasetName) {
                    // Only initialize the variable mappings if there is no existing entry in the map
                    const datastreamVariables = rule.variable_mappings[retrievedVariable.name];
                    if (!datastreamVariables) {
                        rule.variable_mappings[retrievedVariable.name] = [retrievedVariable.name];
                    }
                }
            });

            // If this dataset is being used in a coordinate system mapped grid, then we have to add an array for new
            // datastream variable mappings to the coordinate system
            ProcessUtils.visitDimensions(this.process, (dimension) => {
                if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING &&
                    dimension.input_dataset === inputDatasetName) {
                    // Copy the coordinate variable mapping from the previous rule (likely they will be the same (i.e., time))
                    let coordinateVariable = [];
                    const length = dimension.coordinate_variables_per_rule.length;
                    if (length > 0) {
                        coordinateVariable = dimension.coordinate_variables_per_rule[length - 1];
                    }
                    dimension.coordinate_variables_per_rule.push(coordinateVariable);
                }
            });
        }
    }

    /**
     * update any empty variable mappings after import wizard
     * @param inputDatasetName
     * @param retrievedVariableName
     */
    updateInputDatasetMappingByVariable(inputDatasetName, retrievedVariableName) {
        ProcessUtils.visitInputDatasetRules(this.process, inputDatasetName, (rule) => {

            const dodVar = ProcessUtils.getDatastreamVariable(rule.datastream_name, retrievedVariableName);
            //const datastreamVariableNames = ProcessUtils.guessInputDatastreamVariableNames(this.process, inputDatasetName, retrievedVariableName, rule.datastream_name);

            if (!rule.variable_mappings[retrievedVariableName]) {
                rule.variable_mappings[retrievedVariableName] = [retrievedVariableName];
                if (dodVar && dodVar.renamed && dodVar.renamed.length > 0) {
                    rule.variable_mappings[retrievedVariableName].push(...dodVar.renamed);
                }
            }
        });
    }

    /**
     * Look at the last rule that was just created, and it if is identical to the previous rule,
     * then move the variables up to the previous rule and delete the last rule.
     * NOTE:  This should ONLY be called from the ImportVariablesWizard
     * TODO: This code should be moved inside ImportVariablesWizard since that is the only place it should be called.
     * @param inputDatasetName
     */
    mergeLastRuleCreatedInSameDataset(inputDatasetName) {
        const dataset = this.process.variable_retrieval.input_datasets[inputDatasetName];
        const rules = dataset.rules;

        if (rules.length > 1) {
            const getRuleForComparison = (rule) => {
                // We have to remove the variable mappings & priority when comparing rule for equality
                const ruleClone = _.cloneDeep(rule);
                delete ruleClone.variable_mappings;
                delete ruleClone.priority;
                return ruleClone;
            };

            // We are comparing the last rule to the others
            const rule2 = rules[rules.length - 1];
            const rule2Comp = getRuleForComparison(rules[rules.length - 1]);

            for (let i = rules.length - 2; i >= 0; i--) {
                let rule1 = rules[i];
                let rule1Comp = getRuleForComparison(rules[i]);

                if (_.isEqual(rule1Comp, rule2Comp)) {
                    // move the variable mappings
                    Object.keys(rule2.variable_mappings).forEach(retVarName => {
                        rule1.variable_mappings[retVarName] = rule2.variable_mappings[retVarName];
                    });

                    // then the delete the last rule
                    rules.splice(rules.length -1, 1);

                    //then delete the coordinate mapping
                    ProcessUtils.visitDimensions(this.process, (dimension) => {
                        if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING &&
                            dimension.input_dataset === inputDatasetName) {
                            dimension.coordinate_variables_per_rule.splice(dimension.coordinate_variables_per_rule.length - 1, 1);
                        }
                    });
                    break;
                }
            }

        }

    }

    /**
     * Loop through all the other datasets, and if there is another dataset with the same rule
     * as this one, then merge this dataset's rule to the other dataset and delete this dataset.
     * NOTE:  This should ONLY be called from the ImportVariablesWizard
     * TODO: This code should be moved inside ImportVariablesWizard since that is the only place it should be called.
     * TODO: This code is confusing and can be simplified
     */
    mergeRulesToOtherDatasets(newDatasetName) {

        const getRulesForComparison = (rules) => {
            // We have to remove the variable mappings when comparing rules for equality
            const rulesClone = _.cloneDeep(rules);
            rulesClone.forEach(rule => {
                delete rule.variable_mappings;
            });
            return rulesClone;
        };

        const inputDatasets = this.process.variable_retrieval.input_datasets;
        const keys = Object.keys(inputDatasets);

        // Remove the new dataset from the list of other datasets to iterate over
        _.pull(keys, newDatasetName);

        let dataset2 = null;
        let rules2 = null;
        const datasetName2 = newDatasetName;
        if (inputDatasets[datasetName2]) {
            dataset2 = inputDatasets[datasetName2];
            rules2 = getRulesForComparison(dataset2.rules);
        }
        
        let datasetDeleted = false;

        keys.forEach(datasetName1 => {
            if (!datasetDeleted) {
                const dataset1 = inputDatasets[datasetName1];
                const rules1 = getRulesForComparison(dataset1.rules);

                if (_.isEqual(rules1, rules2)) {

                    // First merge the offsets
                    const offsets = dataset1.offsets;
                    if (dataset1.offsets[0] < dataset2.offsets[0]) {
                        offsets[0] = dataset2.offsets[0];
                    }
                    if (dataset1.offsets[1] < dataset2.offsets[1]) {
                        offsets[1] = dataset2.offsets[1];
                    }
                    dataset1.offsets = offsets;

                    // loop through the dataset2 variables to merge with dataset1
                    ProcessUtils.visitRetrievedVariables(this.process, (retrievedVariable) => {
                        if (retrievedVariable.input_dataset === datasetName2) {
                            // merge the variable mappings
                            dataset1.rules.forEach((rule, index) => {
                                rule.variable_mappings[retrievedVariable.name] = dataset2.rules[index].variable_mappings[retrievedVariable.name];
                            });

                            // switch to use dataset1
                            retrievedVariable.input_dataset = datasetName1;
                        }
                    });

                    // Now remove dataset2
                    delete inputDatasets[datasetName2];
                    datasetDeleted = true; // we do this since we can't break out of a foreach loop :(
                }
            }

        });
    }

    renameRetrievedVariable(oldName, newName, navigateToNewVariableForm) {

        //1. rename in input_dataset's variable mappings
        ProcessUtils.visitRetrievedVariables(this.process, (retrievedVariable) => {
            const inputDataset = this.process.variable_retrieval.input_datasets[retrievedVariable.input_dataset];
            inputDataset.rules.forEach((rule, i) => {
                if (rule.variable_mappings[oldName]) {
                    rule.variable_mappings[newName] = rule.variable_mappings[oldName];
                    delete rule.variable_mappings[oldName];
                }
            });
        });


        //2. rename in retrieved_variables (name and key)
        // Keep the same position in the map so that when editing in the retrieval rules table the variable row
        // doesn't shift positions
        const retrievedVariables = {};
        Object.values(this.process.variable_retrieval.retrieved_variables).forEach(retrievedVariable => {
            if (retrievedVariable.name === oldName) {
                retrievedVariable.name = newName;
                retrievedVariables[newName] = retrievedVariable;
            } else {
                retrievedVariables[retrievedVariable.name] = retrievedVariable;
            }
        });
        this.process.variable_retrieval.retrieved_variables = retrievedVariables;

        //3. rename in output_datastream_variable_mappings
        const mappedDatastreams = this.process.variable_retrieval.output_datastream_variable_mappings;
        Object.keys(mappedDatastreams).forEach(datastreamName => {
            const datastreamMapping = mappedDatastreams[datastreamName];
            const mappedVariables = Object.keys(datastreamMapping);
            if (mappedVariables.indexOf(oldName) > -1) {
                const dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
                const dodVars = dodsById[datastreamName];
                const vars = Object.keys(dodVars).filter(name => name !== 'cVars');

                if (dodVars && vars.length > 0) {
                    datastreamMapping[newName] = datastreamMapping[oldName];
                } else {
                    //rename output variable only if output dod has no variables
                    datastreamMapping[newName] = newName;
                }

                delete datastreamMapping[oldName];
            }
        });

        //4. rename in transformation variable overrides
        this.renameTransformParameterVariableName(oldName, newName);

        if (navigateToNewVariableForm) {
            this.postCommitCallbacks.push(() => {
                NavigationUtils.gotoRetrievedVariable(this.process.name, null, newName);
            });
        }
    }

    renameTransformParameterVariableName = (oldRetrievedVariableName, newRetrievedVariableName) => {
        ProcessUtils.visitTransformationParameters(this.process, (parameter) => {
            const parameterName = parameter.name;

            this.process = ProcessUtils.mapTransformationVariables(this.process, parameterName, (variableObj) => {
                if (variableObj.name === oldRetrievedVariableName) {
                    variableObj.name = newRetrievedVariableName;
                }
                return variableObj;
            });
        });
    };

    syncDeletedRulesWithCoordinateSystem(inputDatasetName, deletedRuleIndexes) {
        ProcessUtils.visitDimensions(this.process, (dimension) => {
            if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING &&
                dimension.input_dataset === inputDatasetName) {
                const newCoordinateVariablesPerRule = [];

                for (let i = 0; i < dimension.coordinate_variables_per_rule.length; i++) {
                    if (!_.includes(deletedRuleIndexes, i)) {
                        newCoordinateVariablesPerRule.push(dimension.coordinate_variables_per_rule[i]);
                    }
                }

                dimension.coordinate_variables_per_rule = newCoordinateVariablesPerRule;
            }
        });
    }

    setCoordinateVariablesPerRule(coordinateSystemName, dimensionName, inputDatasetName, coordinateVariables) {
        // If not explicitly passed in, then if the input datastream has a variable of the same name as this dimension,
        // then we automatically assign it (TODO: check if this dimension has been renamed from that in the original input stream)
        if (!coordinateVariables) {
            const datastreamName = ProcessUtils.getPrimaryDatastream(this.process, inputDatasetName);
            if (ProcessUtils.getDatastreamCoordinateVariable(datastreamName, dimensionName)) {
                coordinateVariables = [dimensionName];
            } else {
                coordinateVariables = []; // we can't determine what this should default to
            }
        }

        const coordinateVariablesPerRule = [];

        this.process.variable_retrieval.input_datasets[inputDatasetName].rules.forEach(rule => {
            coordinateVariablesPerRule.push(coordinateVariables);
        });
        const variablesPerRulePath = ['variable_retrieval', 'coordinate_systems', coordinateSystemName, 'dimensions', dimensionName, 'coordinate_variables_per_rule'];
        this.updateField(variablesPerRulePath, coordinateVariablesPerRule);
    }

    /**
     * Use this instead of addDimension to makes sure the fields are initialized properly.
     * Only call for a new dimension, since it will blow away any previous mapping setting.
     * TODO: this should be refactored into addDimension to avoid confusion.
     *
     * @param coordinateSystemName
     * @param dimensionName
     */
    initializeDimension(coordinateSystemName, dimensionName) {
        this.addDimension(coordinateSystemName, dimensionName); // only adds if it doesn't already exist

        // Always default a dimension to mapping
        const inputDatasets = ProcessUtils.getInputDatasetsForDimension(this.process, dimensionName);
        const inputDatasetName = inputDatasets[0];

        if (inputDatasetName) {
            this.updateDimensionInputDataset(coordinateSystemName, dimensionName, inputDatasetName);

            if (dimensionName !== 'time') {
                // Update non-time dimension to have default unit value
                let units = 'unitless';
                const datastreamName = ProcessUtils.getPrimaryDatastream(this.process, inputDatasetName);
                const coordinateVariableUnits = ProcessUtils.getDimensionCoordinateVariableUnits(this.process, dimensionName, datastreamName);
                if (coordinateVariableUnits.length > 0) {
                    units = coordinateVariableUnits[0];
                }

                this.updateField(['variable_retrieval', 'coordinate_systems', coordinateSystemName, 'dimensions', [dimensionName], 'coordinate_variable_units'], units);
            }
        }
    }

    /**
     * See if there are any conflicts which would require a UI prompt if the given variables
     * were added to the given coordinate system.
     * @param {*} coordSysName
     * @param {*} variableNames
     */
    getRetrievedVariableCoordinateSystemConflicts(coordSysName, variableNames) {
        const coordinateSystemName = coordSysName;
        const variableDimensionConflicts = {};

        if (coordinateSystemName !== null) {
            const coordSysDimensions = ProcessUtils.getDimensionNamesAsArray(this.process, coordinateSystemName);

            variableNames.forEach(variableName => {
                const variable = this.process.variable_retrieval.retrieved_variables[variableName];
                const dimensionChoices = [...coordSysDimensions];
                const missingDimensions = [];

                variable.dims.forEach((variableDimension) => {
                    const coordDimIndex = _.indexOf(coordSysDimensions, variableDimension);

                    if (coordDimIndex >= 0) {
                        // We found this dimension, so remove it from the choices list
                        dimensionChoices.splice(coordDimIndex, 1);

                    } else {
                        missingDimensions.push(variableDimension);
                    }

                });

                // If the missing dimension has no possible rename options, then it will be just added to the dimension,
                // so we don't consider this a conflict
                if (dimensionChoices.length > 0) {
                    missingDimensions.forEach(variableDimension => {
                        if (!variableDimensionConflicts[variableName]) {
                            variableDimensionConflicts[variableName] = {};
                        }
                        variableDimensionConflicts[variableName][variableDimension] = dimensionChoices;
                    });
                }
            });
        }
        return variableDimensionConflicts;
    }

    addGeospatialVariables(inputDatasetName) {
        let datasetName = inputDatasetName;

        // First remove any previous alt/lat/lon variables
        const selectedItems = [];
        ProcessUtils.visitRetrievedVariables(this.process, retrievedVariable => {
            if (retrievedVariable.name === 'alt' || retrievedVariable.name === 'lat' || retrievedVariable.name === 'lon') {
                selectedItems.push(retrievedVariable.name);
            }
        });
        if (selectedItems.length > 0) {
            this.deleteEntities('variable_retrieval.retrieved_variables', selectedItems);
        }

        // If no input dataset name defined, then get the first valid one
        if (!inputDatasetName) {
            const datasetNames = ProcessUtils.getDatasetsWithGeospatialVariables(this.process);
            if (datasetNames.length > 0) {
                datasetName = datasetNames[0];
            }
        }

        // Now add the variables
        this.addRetrievedVariable(datasetName, 'alt');
        this.addRetrievedVariable(datasetName, 'lat');
        this.addRetrievedVariable(datasetName, 'lon');

        // Make sure that the variables are not required, so it doesn't mess with wizard settings
        this.process.variable_retrieval.retrieved_variables.alt.variable_required = 0;
        this.process.variable_retrieval.retrieved_variables.lat.variable_required = 0;
        this.process.variable_retrieval.retrieved_variables.lon.variable_required = 0;
    }

    addRetrievedVariable(inputDatasetName, retrievedVariableName) {
        // Get the existing variables using that dataset
        const existingVarNames = ProcessUtils.getRetrievedVariablesForDataset(this.process, inputDatasetName);
        const ruleDatastreams = {};

        // Add new variable mappings to each rule in the dataset
        ProcessUtils.visitInputDatasetRules(this.process, inputDatasetName, (rule) => {

            rule.variable_mappings[retrievedVariableName] = [retrievedVariableName];
            const dodVar = ProcessUtils.getDatastreamVariable(rule.datastream_name, retrievedVariableName);
            if (dodVar && dodVar.renamed && dodVar.renamed.length > 0) {
                rule.variable_mappings[retrievedVariableName].push(...dodVar.renamed);
            }

            ruleDatastreams[rule.datastream_name] = '';
        });

        // We also have to add the new retrieved variable
        const newRetrievedVariable = _.cloneDeep(Constants.RETRIEVED_VARIABLE_NEW);
        newRetrievedVariable.name = retrievedVariableName;
        newRetrievedVariable.input_dataset = inputDatasetName;

        // default to the coordinate system and output datastream from another variable in that dataset
        if (existingVarNames.length > 0) {
            const referenceVar = this.process.variable_retrieval.retrieved_variables[existingVarNames[0]];
            newRetrievedVariable.coordinate_system = referenceVar.coordinate_system;
            const outputDatastreams = ProcessUtils.getRetrievedVariableOutputDatastreams(this.process, referenceVar.name);
            if (outputDatastreams.length > 0) {
                const outputDs = outputDatastreams[0];
                this.process.variable_retrieval.output_datastream_variable_mappings[outputDs][retrievedVariableName] = retrievedVariableName;
            }

            // If we can find the variable name in one of the datastreams for the dataset, then we can set the
            // dimensions for that dataset and initialize the transform parameters
            let varDims = null;
            Object.keys(ruleDatastreams).forEach(datastreamName => {
                const dodVar = ProcessUtils.getDatastreamVariable(datastreamName, retrievedVariableName);
                if (dodVar && !varDims) {
                    varDims = dodVar.dims;
                }
            });

            if (varDims) {
                newRetrievedVariable.dims = varDims;

                // see if we need to set the transformation type for this var/dimension
                if (referenceVar.coordinate_system) {
                    varDims.forEach(dimensionName => {
                        const ttype = ProcessUtils.getVariableTransformationTypeOverride(this.process, referenceVar.name, dimensionName);
                        if (ttype === Constants.TRANSFORM_TYPE_PASSTHROUGH) {
                            this.addTransformationParameterOverrideVariable('transformation_type', dimensionName, null, retrievedVariableName, ttype);
                        }
                    });
                }
            }
        }

        this.process.variable_retrieval.retrieved_variables[retrievedVariableName] = newRetrievedVariable;
    }

    /**
     * Add the given variables to the given coordinate system.
     * Note that if no resolutions are provided and the variable has a dimension that isn't in the coordinate system, then
     * this dimension will be added to the end of the coordinate system.
     * @param {*} coordSysName
     * @param {*} variableNames
     * @param {*} resolutions
     */
    addRetrievedVariablesToCoordinateSystem(coordSysName, variableNames, resolutions) {
        const coordinateSystemName = coordSysName;

        variableNames.forEach(variableName => {
            const coordSysDimensions = ProcessUtils.getDimensionNamesAsArray(this.process, coordinateSystemName);
            const variable = this.process.variable_retrieval.retrieved_variables[variableName];
            variable.coordinate_system = coordinateSystemName;

            if (coordinateSystemName === null) {
                // If coordinate system name is null, then we are removing the coordinate system
                // and we have to revert the dimensions of the variable back to the ones from the input stream
                variable.dims = ProcessUtils.getRetrievedVariableDimensionsFromInputstream(this.process, variableName);

            } else {
                variable.dims.forEach((variableDimension, index) => {
                    let resolution = null;
                    let dimensionName = variableDimension;
                    if (resolutions && resolutions[variableName] && resolutions[variableName][variableDimension]) {
                        resolution = resolutions[variableName][variableDimension];
                    }

                    if (resolution) {
                        if (resolution.action === 'add') {
                            // Add the variable's dimension to the end of the coordinate system dims
                            this.initializeDimension(coordinateSystemName, variableDimension);

                        } else {
                            // Rename the variable's dimension to match one from the coordinate system
                            // Find item index 
                            const i = _.findIndex(variable.dims, variableDimension);

                            // Replace item at index using splice
                            variable.dims.splice(i, 1, resolution.newDimensionName);
                            dimensionName = resolution.newDimensionName;
                        }

                    } else if (coordSysDimensions.indexOf(variableDimension) === -1) {
                        // If we have no resolution for this dimension but it doesn't exist, we add it to 
                        // the end of the coordinate system
                        this.initializeDimension(coordinateSystemName, variableDimension);
                    }
                });
            }
        });
    }

    updateMultipleRetrievedVariables(originalRetrievedVariableNames, newValues, resolutions) {

        // First make sure that if the user has typed in new output datastreams, they are added if they don't exist
        if (newValues.output_datastreams !== null) {
            newValues.output_datastreams.forEach(datastreamName => {
                this.addOutputDatastream(datastreamName);
            });
        }

        // Now rename variables if the name prefix changed
        const retrievedVariableNames = [];
        originalRetrievedVariableNames.forEach((oldName) => {
            const originalPrefix = newValues.originalPrefix ? `${newValues.originalPrefix}_` : '';
            let prefix = newValues.prefix ? newValues.prefix : '';
            if (prefix && !prefix.endsWith('_')) {
                prefix += '_';
            }
            if (originalPrefix == prefix) {
                // No change to variable name
                retrievedVariableNames.push(oldName);

            } else {
                let newName;

                if (originalPrefix) {
                    // Replace the original prefix with the new prefix
                    newName = oldName.replace(originalPrefix, prefix);

                } else {
                    // Prepend the new prefix to the name
                    newName = `${prefix}${oldName}`;
                }
                this.renameRetrievedVariable(oldName, newName);
                retrievedVariableNames.push(newName);
            }
        });

        // Now create any new datasets if needed
        if (newValues.inputDataset && !this.process.variable_retrieval.input_datasets[newValues.inputDataset]) {
            const datastreams = ProcessUtils.getRetrievedVariableInputDatastreams(this.process, retrievedVariableNames);
            this.createInputDataset(newValues.inputDataset, datastreams);
        }

        retrievedVariableNames.forEach((variableName) => {
            const variableCopy = this.process.variable_retrieval.retrieved_variables[variableName];

            // if newValue has null that means to not overwrite it (nothing was selected)
            variableCopy.data_type = newValues.data_type !== null ? newValues.data_type : variableCopy.data_type;
            variableCopy.units = newValues.units !== null ? newValues.units : variableCopy.units;

            if (newValues.variable_required !== '') {
                variableCopy.variable_required = newValues.variable_required ? 1 : 0;
            }
            if (newValues.retrieve_qc_variable !== '') {
                variableCopy.retrieve_qc_variable = newValues.retrieve_qc_variable ? 1 : 0;
            }
            if (newValues.qc_variable_required !== '') {
                variableCopy.qc_variable_required = newValues.qc_variable_required ? 1 : 0;
            }
            if (newValues.output_datastreams !== null) {
                this.setVariableOutputDatastreams(variableName, newValues.output_datastreams);
            }
            const oldDataset = variableCopy.input_dataset;
            if (newValues.inputDataset && newValues.inputDataset != oldDataset) {
                this.setInputDatasetForVariable(newValues.inputDataset, variableName);
            }

            Object.keys(newValues.variableDimensions).forEach(dimensionName => {
                const newName = newValues.variableDimensions[dimensionName];
                if (newName) {
                    this.renameVariableDimension(variableName, dimensionName, newName);
                    if (variableCopy.coordinate_system !== null) {
                        const coordinateSystemDimensions = ProcessUtils.getDimensionNamesAsArray(this.process, variableCopy.coordinate_system);
                        if (coordinateSystemDimensions.indexOf(newName) === -1) {
                            this.addDimension(variableCopy.coordinate_system, newName);
                        }
                    }
                }
            });
        });

        if (newValues.coordinate_system !== undefined) {
            this.addRetrievedVariablesToCoordinateSystem(newValues.coordinate_system, retrievedVariableNames, resolutions);
        }

        if (newValues.isOverrideTableVisible !== null && !newValues.isOverrideTableVisible) {
            this.updateTransformOverrideForVariables('transformation_type', retrievedVariableNames, false);
        } else {
            Object.keys(newValues.overrideValues).forEach(coordinateSystemName => {
                Object.keys(newValues.overrideValues[coordinateSystemName]).forEach(dimensionName => {
                    const value = newValues.overrideValues[coordinateSystemName][dimensionName];
                    if (value !== null) {
                        this.updateTransformOverrideValueForVariables('transformation_type', retrievedVariableNames, coordinateSystemName, dimensionName, value);
                    }
                });
            });
        }
    }

    removeOutputDatastreamMappingsForVariable(outputDatastreamMappings, variableName) {

        Object.keys(outputDatastreamMappings).forEach(datastreamName => {
            Object.keys(outputDatastreamMappings[datastreamName]).forEach(mappedVariableName => {
                if (mappedVariableName === variableName) {
                    delete outputDatastreamMappings[datastreamName][variableName];
                }
            });
        });
    }

    /**
     * Some RIE fields need to be parsed into the correct format.  This only parses generic RIE values.
     * Logic for specific fields needs to be added to the appropriate update method.
     * @param fieldPath
     * @param fieldValue
     * @returns {*}
     */
    getFormattedValue(fieldValue) {
        let value = fieldValue;

        // Some fields needs to be parsed into the correct format
        const Set = require('es6-set');

        if (value instanceof Set) { // this is a tags box
            value = [...fieldValue]; // default is to use the array values as strings

        } else if (value instanceof Object) { // this is a select box
            if ('id' in value) {
                value = value.id; // RIEComboBox

            } else if ('value' in value) {
                value = value.value; // RIEComboBox (based on react-select)
            }
        } else if (typeof fieldValue === 'string') {
            // trim any white space
            value = fieldValue.trim();
        }
        return value;
    }

    removeUnusedCoordinateSystems() {
        const usedCoordinateSystems = [];
        ProcessUtils.visitRetrievedVariables(this.process, retrievedVariable => {
            if (usedCoordinateSystems.indexOf(retrievedVariable.coordinate_system) === -1) {
                usedCoordinateSystems.push(retrievedVariable.coordinate_system);
            }
        });

        const unusedCoordinateSystems = _.difference(Object.keys(this.process.variable_retrieval.coordinate_systems), usedCoordinateSystems);
        this.deleteEntities('variable_retrieval.coordinate_systems', unusedCoordinateSystems);
    }

    /**
     * For the given dimension, if there are transformation type variable overrides set to passthrough but
     * those variables do not come from the same dataset that the dimension is mapped to, then we remove those overrides.
     * @param dimension
     */
    removePassthroughForDirectMappedVariables = (coordinateSystemName, dimensionName) => {
        const dimension = this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName];

        // Does not apply if the dimension has default transformation type of passthough
        const transformationTypeDefault = ProcessUtils.getDimensionTransformationType(this.process, coordinateSystemName, dimensionName);

        if (transformationTypeDefault !== Constants.TRANSFORM_TYPE_PASSTHROUGH) {

            const inputDatasetName = dimension.input_dataset;

            // Loop through all the variables and remove them from transformation_type variable overrides if they are PASSTHROUGH
            const oldVariables = this.process.variable_retrieval.transformation_parameters.transformation_type.variables;
            const newVariables = [];

            oldVariables.forEach(overrideVariable => {
                const variableInputDataset = ProcessUtils.getInputDatasetForVariable(this.process, overrideVariable.name);
                const isVariablePassthroughInCurrentDataset =
                    variableInputDataset === inputDatasetName &&
                    overrideVariable.dim === dimension.name &&
                    overrideVariable.value === Constants.TRANSFORM_TYPE_PASSTHROUGH;

                if (!isVariablePassthroughInCurrentDataset) {
                    newVariables.push(overrideVariable);
                }
            });
            this.process.variable_retrieval.transformation_parameters.transformation_type.variables = newVariables;

        }
    };

    /**
     * For the given dimension, make sure that all the variables that come from the same dataset that is mapped to
     * that dimension have transformation type variable overrides set to passthrough.
     * @param dimension
     */
    setPassthroughForDirectMappedVariables = (coordinateSystemName, dimensionName) => {
        const dimension = this.process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName];

        // Does not apply if the dimension has default transformation type of passthough
        const transformationTypeDefault = ProcessUtils.getDimensionTransformationType(this.process, coordinateSystemName, dimensionName);

        if (transformationTypeDefault !== Constants.TRANSFORM_TYPE_PASSTHROUGH) {
            const inputDatasetName = dimension.input_dataset;

            // We need to visit all the transformation variable overrides for transformation_type and remove any
            // passthrough overrides for variables that are NOT in the given input dataset
            const oldVariables = this.process.variable_retrieval.transformation_parameters.transformation_type.variables;
            const newVariables = [];
            oldVariables.forEach(overrideVariable => {
                const variableInputDataset = ProcessUtils.getInputDatasetForVariable(this.process, overrideVariable.name);
                const isVariablePassthroughButNotInCurrentDataset =
                    variableInputDataset !== inputDatasetName &&
                    overrideVariable.dim === dimension.name &&
                    overrideVariable.value === Constants.TRANSFORM_TYPE_PASSTHROUGH;

                if (!isVariablePassthroughButNotInCurrentDataset) {
                    newVariables.push(overrideVariable);
                }
            });
            this.process.variable_retrieval.transformation_parameters.transformation_type.variables = newVariables;

            // Loop through all the variables that come from the mapped dataset and add them to the transformation_type variable overrides as passthrough
            const retrievedVariableNames = ProcessUtils.getRetrievedVariablesForDataset(this.process, inputDatasetName);

            retrievedVariableNames.forEach(variableName => {
                this.updateTransformationParameterOverride('transformation_type', coordinateSystemName,
                    dimension.name, null, variableName, Constants.TRANSFORM_TYPE_PASSTHROUGH);
            });
        }
    };

    initializeRegularGrid(coordinateSystemName, dimensionName) {
        const dimensionBasePath = ['variable_retrieval', 'coordinate_systems', coordinateSystemName, 'dimensions', dimensionName];
        const dimension = _.get(this.process, dimensionBasePath);

        const exploredDatastreams = DataExplorerService.getExploredDatastreams(this.process.name);
        const datastreamName = ProcessUtils.getPrimaryDatastream(this.process, dimension.input_dataset);

        if (exploredDatastreams && exploredDatastreams[datastreamName] && exploredDatastreams[datastreamName].currentData && exploredDatastreams[datastreamName].currentData.dimensions[dimensionName]) {
            const exploreDimension = exploredDatastreams[datastreamName].currentData.dimensions[dimensionName];

            const start = exploreDimension.start_point;
            const end = exploreDimension.end_point;

            this.updateField([...dimensionBasePath, 'data_transformation', 'end'], end);
            this.updateField([...dimensionBasePath, 'data_transformation', 'start'], start);
        }
    }

    /**
     * If the given dimension is using regular interval for the grid, and start, end, and interval are set, then
     * compute the 4th parameter length from the other 3.
     * @param dimension
     */
    computeAndSetRegularGridLength = (coordinateSystemName, dimensionName) => {
        const dimensionBasePath = ['variable_retrieval', 'coordinate_systems', coordinateSystemName, 'dimensions', dimensionName];
        const dimension = _.get(this.process, dimensionBasePath);

        let length = 0;
        const interval = dimension.data_transformation.interval;
        const start = dimension.data_transformation.start;
        let end = dimension.data_transformation.end;
        if (dimensionName === 'time') {
            end = start + parseInt(this.process.processing_interval, 10) - interval;
            this.updateField([...dimensionBasePath, 'data_transformation', 'end'], end);
        }

        if (!isNaN(interval) && !isNaN(start) && !isNaN(end)) {
            length = ProcessUtils.calculateGridNumberOfIntervals(interval, start, end);
            this.updateField([...dimensionBasePath, 'data_transformation', 'length'], length);
        }
    };

    renameWizardCoordinateSystemDefault = () => {
        const coordinateSystems = this.process.variable_retrieval.coordinate_systems;
        if (Object.keys(coordinateSystems).length > 0) {
            const oldName = coordinateSystems[Object.keys(coordinateSystems)[0]].name;

            //only create this new name if we created a default coordinate system in the wizard
            if (oldName === Constants.DEFAULT_COORDINATE_SYSTEM_NAME) {
                let newName = oldName;
                const dimensions = this.process.variable_retrieval.coordinate_systems[oldName].dimensions;

                Object.keys(dimensions).forEach(dimensionName => {
                    const dimension = dimensions[dimensionName];
                    if (dimensionName === 'time') {
                        switch (dimension.gridding_method) {
                            case Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING:
                                newName = `same_as_${dimension.input_dataset}`;
                                break;
                            case Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID:
                                newName = `${dimension.data_transformation.interval}_second_grid`;
                                break;
                            default:  // stupid linting rule requires a default case
                                break;
                        }
                    }
                });

                this.renameCoordinateSystem(oldName, newName, false);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/utils/ProcessEditor.js